/**
 * Shared relative-volume baseline for the volume-marker family (🦈 CME,
 * 🐋 Vest, and everything built on them: 🐋🦈, 🐱, the 🪝 spike factor).
 *
 * The caller decides which bars are ELIGIBLE (closed, outside a reopen
 * window, the venue open) and feeds them in time order, each as its volume
 * per 2m base slot plus how many slots the bar spans. This walks them once:
 *
 * - trailing: mean of the previous N eligible bars;
 * - tod (time of day): mean of the eligible bars at the same Chicago minute of
 *   day over the previous TOD_SESSIONS occurrences (one per day the venue was
 *   open at that slot; days it was closed have no bar and add nothing), which
 *   absorbs the RTH open/close surges a trailing mean flags every day;
 * - auto: tod when >= TOD_MIN_SESSIONS samples exist, else trailing;
 * - tod forced: the same TOD_MIN_SESSIONS minimum and no fallback. A bar with
 *   fewer gets no rel vol and is reported through `onMiss` (with how many
 *   sessions there were), so the UI can say why instead of showing nothing.
 *
 * Time of day needs ~10 earlier sessions of bars, far more than the chart
 * loads (~1 day of 2m candles). Callers feed BASELINE HISTORY (fetched apart
 * from the chart, see BASELINE_HISTORY_DAYS / CME_BASELINE_CANDLES) ahead of
 * the chart's bars and keep only the chart's bars in the output (#593).
 *
 * A bar's result depends only on itself and EARLIER bars, so adding later
 * bars never changes it (no repaint).
 */
export const TOD_SESSIONS = 10;
export const TOD_MIN_SESSIONS = 5;

/** Vest baseline history: calendar days fetched behind the chart (Vest closes weekends: ~11-12 open days). */
export const BASELINE_HISTORY_DAYS = 16;
/**
 * CME baseline history: 2m candles fetched behind the chart. A CME session is
 * ~690 2m bars, so 12 x 720 = ~12.5 sessions: TOD_SESSIONS plus margin for a
 * holiday or short session (~1.07 MB of JSON, once per symbol per day).
 */
export const CME_BASELINE_CANDLES = 12 * 720;

/** Start of the Vest baseline window: UTC midnight BASELINE_HISTORY_DAYS ago (stable for a whole day). */
export function baselineFromMs(nowMs: number): number {
  return Math.floor(nowMs / 86_400_000) * 86_400_000 - BASELINE_HISTORY_DAYS * 86_400_000;
}

export type RelVolBaseline = 'auto' | 'trailing' | 'tod';
export type RelVolBasis = 'trailing' | 'tod';

export const clampBaseline = (b: unknown): RelVolBaseline => (b === 'trailing' || b === 'tod' || b === 'auto' ? b : 'auto');

export interface RelVolHit {
  relVol: number;
  /** Baseline volume for a bar of this size (per-slot mean x slots). */
  avg: number;
  basis: RelVolBasis;
  /** Bars (trailing) or sessions (time of day) behind the baseline. */
  samples: number;
}

/** An eligible bar forced time of day could not rate (fewer than TOD_MIN_SESSIONS sessions). */
export interface RelVolMiss {
  basis: 'tod';
  /** Sessions at that time of day that were available. */
  samples: number;
}

/**
 * Rel vols keyed by bar time (seconds), plus the bars forced time of day could
 * not rate. Returned as a plain Map type (callers and props keep their types);
 * read the misses with `relVolMisses`.
 */
export class RelVolMap<T> extends Map<number, T> {
  readonly misses = new Map<number, RelVolMiss>();
}

const NO_MISSES: ReadonlyMap<number, RelVolMiss> = new Map();

/** The bars forced time of day could not rate (empty for any other map). */
export function relVolMisses(map: ReadonlyMap<number, unknown> | null | undefined): ReadonlyMap<number, RelVolMiss> {
  return map instanceof RelVolMap ? map.misses : NO_MISSES;
}

export interface BaselinePoint {
  /** Bar open, seconds. */
  time: number;
  /** Volume per 2m base slot. */
  perSlot: number;
  /** 2m base slots the bar spans (avg = per-slot mean x slots). */
  slots: number;
}

const ctOffsetCache = new Map<number, number>();
const ctFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

/** Minutes of the Chicago day for an epoch ms (DST-aware, cached per hour). */
export function chicagoMinuteOfDay(ms: number): number {
  const hour = Math.floor(ms / 3_600_000);
  let off = ctOffsetCache.get(hour);
  if (off === undefined) {
    const p = Object.fromEntries(ctFmt.formatToParts(ms).map((x) => [x.type, x.value]));
    const local = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute));
    off = Math.round((local - Math.floor(ms / 60_000) * 60_000) / 60_000);
    ctOffsetCache.set(hour, off);
  }
  const m = Math.floor(ms / 60_000) + off;
  return ((m % 1440) + 1440) % 1440;
}

/**
 * Walk eligible points (time order) and call `onHit` for every one that has
 * a baseline under the chosen mode, `onMiss` for one forced time of day
 * cannot rate. Every point joins both baselines after it is evaluated.
 *
 * Cost: ~1.7 ms for 12 days of 2m bars (measured), so it is simply re-run;
 * callers cache per data change (ChartView per bar close, vestRelVolsFor per
 * Vest store update).
 */
export function rollingRelVols<P extends BaselinePoint>(
  points: Iterable<P>,
  lookback: number,
  baseline: RelVolBaseline,
  onHit: (p: P, hit: RelVolHit) => void,
  onMiss?: (p: P, miss: RelVolMiss) => void,
): void {
  const trail: number[] = [];
  let trailSum = 0;
  const tod = new Map<number, number[]>();
  for (const p of points) {
    const todKey = chicagoMinuteOfDay(p.time * 1000);
    const todHist = tod.get(todKey) ?? [];
    if (baseline !== 'trailing' && todHist.length >= TOD_MIN_SESSIONS) {
      const avg = todHist.reduce((a, x) => a + x, 0) / todHist.length;
      if (avg > 0) onHit(p, { relVol: p.perSlot / avg, avg: avg * p.slots, basis: 'tod', samples: todHist.length });
    } else if (baseline === 'tod') {
      onMiss?.(p, { basis: 'tod', samples: todHist.length });
    } else if (trail.length === lookback && trailSum > 0) {
      const avg = trailSum / lookback;
      onHit(p, { relVol: p.perSlot / avg, avg: avg * p.slots, basis: 'trailing', samples: lookback });
    }

    trail.push(p.perSlot);
    trailSum += p.perSlot;
    if (trail.length > lookback) trailSum -= trail.shift() as number;
    todHist.push(p.perSlot);
    if (todHist.length > TOD_SESSIONS) todHist.shift();
    tod.set(todKey, todHist);
  }
}

/** "time of day: 3/5 sessions, not enough history" (forced time of day could not rate a bar). */
export function todMissText(samples: number): string {
  return `time of day: ${samples}/${TOD_MIN_SESSIONS} sessions, not enough history`;
}

/** "same time of day, 7 sessions" / "previous 30 bars" (tooltips). */
export function basisDetail(r: { basis: RelVolBasis; samples: number }): string {
  return r.basis === 'tod' ? `same time of day, ${r.samples} sessions` : `previous ${r.samples} bars`;
}

/** "vs same time, 7 sessions" / "vs prev 30 bars" (compact, volume pane legend). */
export function relVolBasisText(r: { basis: RelVolBasis; samples: number }): string {
  return r.basis === 'tod' ? `vs same time, ${r.samples} sessions` : `vs prev ${r.samples} bars`;
}
